import { useLingui } from "@lingui/react";
import { useAxios } from "../../utils/hooks";
import React, { useEffect, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import moment from "moment/moment";
import { openNotification } from "../../utils/openNotification";
import { Button, Card, DatePicker, Divider, Form, Input, Radio, Space, Tooltip, Upload, UploadProps } from "antd";
import { FolderOpenOutlined, InfoCircleTwoTone, ProfileOutlined, UploadOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import FileDownload from "js-file-download";
import useSafeState from "../../utils/safe-state";
import { Trans } from "@lingui/macro";
import L from "leaflet";

interface Interface {
    data?: any;
    form?: any;
}

const OnboardingSecondStep = (props: Interface) => {

    const data = props.data || {};
    const form = props.form || {};

    const { i18n } = useLingui();
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [circumventionRiskValue, setCircumventionRiskValue] = useState<number | undefined>(undefined);
    const [certificationFileList, setCertificationFileList] = useSafeState<Array<any>>([]);
    const [certificationLoading, setCertificationLoading] = useSafeState(false);
    const [certEgFileList, setCertEgFileList] = useSafeState<Array<any>>([]);
    const [certEgLoading, setCertEgLoading] = useSafeState(false);
    const [reviewReportFileList, setReviewReportFileList] = useSafeState<Array<any>>([]);
    const [reviewReportLoading, setReviewReportLoading] = useSafeState(false);
    const [finProdGoodsMixing, setFinProdGoodsMixing] = useSafeState(false);
    const [rawMatGoodsMixing, setRawMatGoodsMixing] = useSafeState(false);

    const getDetailedErrorMessage = (error: any): string => {
        let errorMessage = 'An unexpected error occurred. Please try again.';

        if (error.response) {
            if (error.response.data) {
                if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                }
            }

            if (errorMessage === 'An unexpected error occurred. Please try again.') {
                if (error.response.status === 400) {
                    errorMessage = 'Invalid file format. Please check:\n' +
                        '• Headers must be: LOCATION NAME, LOCATION GEOJSON\n' +
                        '• Only MULTIPOLYGON format is accepted\n' +
                        '• All rows must have both name and polygon data\n' +
                        '• Format: MULTIPOLYGON (((lng lat, lng lat, lng lat, lng lat)))';
                } else if (error.response.status === 500) {
                    errorMessage = 'Server error while processing your file. Please verify:\n' +
                        '• File is not corrupted\n' +
                        '• Polygon coordinates are valid\n' +
                        '• No special characters in location names';
                }
            }
        } else if (error.request) {
            errorMessage = 'Unable to reach the server. Please check your internet connection and try again.';
        } else if (error.message) {
            errorMessage = error.message;
        }

        return errorMessage;
    };


    useEffect(() => {
        getProfileData();
        getAnyFinishedProductGoodsMixing();
        getAnyRawMaterialGoodsMixing();
    }, []);

    const getAnyFinishedProductGoodsMixing = () => {

        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/item-data/any-finished-product-goods-mixing', {})
                .then(function (response: AxiosResponse<any>) {
                    setFinProdGoodsMixing(response.data);
                })
                .catch(function (error) {
                    openNotification('error', error);
                })
                .finally(() => {
                });
        }
    };

    const getAnyRawMaterialGoodsMixing = () => {

        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/item-data/any-raw-material-goods-mixing', {})
                .then(function (response: AxiosResponse<any>) {
                    setRawMatGoodsMixing(response.data);
                })
                .catch(function (error) {
                    openNotification('error', error);
                })
                .finally(() => {
                });
        }
    };


    const getProfileData = () => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/onboarding-data/get-second-step-data', {})
                .then(function (response: AxiosResponse<any>) {

                    const data = response.data;

                    if (data.goodsMixingCounterMeasureDeadline) {
                        data.goodsMixingCounterMeasureDeadline = moment(data.goodsMixingCounterMeasureDeadline);
                    }

                    if (data.rawMatGoodsMixingCounterMeasureDeadline) {
                        data.rawMatGoodsMixingCounterMeasureDeadline = moment(data.rawMatGoodsMixingCounterMeasureDeadline);
                    }

                    if (data.helpfulForIdentifyingCircumventionRisks !== undefined && data.helpfulForIdentifyingCircumventionRisks !== null) {
                        data.helpfulForIdentifyingCircumventionRisks = Number(data.helpfulForIdentifyingCircumventionRisks);
                        setCircumventionRiskValue(data.helpfulForIdentifyingCircumventionRisks);
                    }

                    if (data.haveAnyConcerns !== undefined && data.haveAnyConcerns !== null) {
                        data.haveAnyConcerns = Number(data.haveAnyConcerns);
                    }

                    if (data.haveAnyEarlyStageInvestigation !== undefined && data.haveAnyEarlyStageInvestigation !== null) {
                        data.haveAnyEarlyStageInvestigation = Number(data.haveAnyEarlyStageInvestigation);
                    }

                    form.setFieldsValue({
                        onboarding: {
                            profile: data
                        }
                    });
                })
                .catch(function (error) {
                    openNotification('error', error);
                })
                .finally(() => {
                });
        }
    };

    const certificationProps: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/onboarding-data/upload-doc',
        multiple: true,
        fileList: certificationFileList,
        onChange(info) {
            if (info.file.status === 'error') {
                openNotification('error', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            const list = [...certificationFileList, file];
            setCertificationFileList(list);
            return false;
        },
    };

    const certEgProps: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/onboarding-data/upload-doc',
        multiple: true,
        fileList: certEgFileList,
        onChange(info) {
            if (info.file.status === 'error') {
                openNotification('error', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            const list = [...certEgFileList, file];
            setCertEgFileList(list);
            return false;
        },
    };

    const uploadProfileDocument = (
        onboardingDataType: string,
        fileList: Array<any>,
        setFileList: (value: Array<any>) => void,
        setLoading: (value: boolean) => void
    ) => {
        if (!fileList || fileList.length === 0) {
            openNotification('error', 'No file selected. Please select a file to upload.');
            return;
        }

        if (axiosInstance.current != null) {
            setLoading(true);

            const formData = new FormData();
            fileList.forEach(file => formData.append('files', file));
            formData.append('onboardingDataType', onboardingDataType);

            axiosInstance.current
                .post('/resource/onboarding-data/upload-doc', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then(() => {
                    openNotification('customSuccess', 'File successfully uploaded');
                })
                .catch((error) => {
                    openNotification('error', getDetailedErrorMessage(error));
                })
                .finally(() => {
                    setFileList([]);
                    setLoading(false);
                });
        }
    };

    const reviewReportProps: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/onboarding-data/upload-doc',
        multiple: true,
        fileList: reviewReportFileList,
        onChange(info) {
            if (info.file.status === 'error') {
                openNotification('error', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            const list = [...reviewReportFileList, file];
            setReviewReportFileList(list);
            return false;
        },
    };

    return (
        <Form form={form} component={false}>
            <Card
                title={<><ProfileOutlined style={{ marginRight: 10 }} />Your Business Profile (2/2)</>}
                bordered={true}
                style={{ width: 800, border: '1px solid #39893e' }}
                headStyle={{ backgroundColor: 'darkseagreen' }}
                bodyStyle={{ backgroundColor: '#e1efe1' }}
            >
                <b><Trans>5. If there is a risk to be mixed what countermeasures will be taken and provide the target date? (Finished products)</Trans></b>
                <Form.Item
                    style={{ marginBottom: 10 }}
                    name={["onboarding", "profile", "goodsMixingCounterMeasureDeadline"]}
                    rules={[{ required: finProdGoodsMixing, message: 'Please provide a date' }]}
                >
                    <DatePicker disabled={!finProdGoodsMixing} style={{ marginTop: 10 }} />
                </Form.Item>
                <Form.Item
                    style={{ marginBottom: 10 }}
                    name={["onboarding", "profile", "goodsMixingCounterMeasure"]}
                    rules={[{ required: finProdGoodsMixing, message: 'Please write a countermeasure' }]}
                >
                    <TextArea disabled={!finProdGoodsMixing} rows={4} placeholder="Please write the countermeasure here..." />
                </Form.Item>

                <Divider></Divider>
                <b><Trans>6. If there is a risk to be mixed what countermeasures will be taken and provide the target date? (Raw materials)</Trans></b>
                <Form.Item
                    style={{ marginBottom: 10 }}
                    name={["onboarding", "profile", "rawMatGoodsMixingCounterMeasureDeadline"]}
                    rules={[{ required: rawMatGoodsMixing, message: 'Please provide a date' }]}
                >
                    <DatePicker disabled={!rawMatGoodsMixing} style={{ marginTop: 10 }} />
                </Form.Item>
                <Form.Item
                    style={{ marginBottom: 10 }}
                    name={["onboarding", "profile", "rawMatGoodsMixingCounterMeasure"]}
                    rules={[{ required: rawMatGoodsMixing, message: 'Please write a countermeasure' }]}
                >
                    <TextArea disabled={!rawMatGoodsMixing} rows={4} placeholder="Please write the countermeasure here..." />
                </Form.Item>

                <Divider></Divider>
                <b><Trans>7. Have you identified or deteced the followings in the supply chain: eg. Unusual trade flows / volume mismatches or any suspicious indicates possible circumvention risk? If yes please describe.</Trans></b>
                <Form.Item
                    name={["onboarding", "profile", "helpfulForIdentifyingCircumventionRisks"]}
                    rules={[{ required: true, message: 'Please select an option' }]}
                >
                    <Radio.Group style={{ marginTop: 10 }}
                        onChange={(e) => {
                            setCircumventionRiskValue(e.target.value);
                            form.validateFields([["onboarding", "profile", "helpfulForIdentifyingCircumventionRisksDescription"]]);
                        }}
                    >
                        <Space direction="horizontal">
                            <Radio value={1}>Yes</Radio>
                            <Radio value={2}>No</Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
                <Form.Item
                    style={{ marginTop: 10 }}
                    name={["onboarding", "profile", "helpfulForIdentifyingCircumventionRisksDescription"]}
                    rules={[{ required: circumventionRiskValue === 1, message: 'Please describe the risk' }]}
                >
                    <TextArea rows={4} disabled={circumventionRiskValue !== 1} placeholder="Please write the description..." />
                </Form.Item>

                <Divider></Divider>
                <b><Trans>8. Please provide the Certifications e.g. FSC, RSPO, or Fairtrade. (if any)</Trans></b>
                <Tooltip
                    placement="top"
                    title={
                        <>
                            <div>
                                Certification schemes like Forest Stewardship Council (FSC) or Roundtable on Sustainable Palm Oil (RSPO) require companies to demonstrate consultation with indigenous communities as part of their certification process                            </div>
                        </>
                    }
                    children={
                        <InfoCircleTwoTone
                            twoToneColor="#1890ff"
                            style={{ marginLeft: 5 }}
                        />
                    }
                />
                <Form.Item
                    style={{ marginBottom: 0 }}
                    name={["onboarding", "profile", "certEgFscOrRspoOrFairtrade"]}
                >
                    <div style={{ marginBottom: 10 }}>
                        <Upload {...certEgProps}>
                            <Button
                                style={{ marginTop: 10 }}
                                icon={<FolderOpenOutlined />}
                            > <Trans>Select File</Trans></Button>
                        </Upload>
                    </div>
                </Form.Item>
                <div className="center-card">
                    <Button
                        loading={certEgLoading}
                        htmlType="button"
                        type="primary"
                        onClick={() => uploadProfileDocument(
                            'onboarding.profile.certEgFscOrRspoOrFairtrade',
                            certEgFileList,
                            setCertEgFileList,
                            setCertEgLoading
                        )}
                    >
                        <ButtonWithIcon
                            icon={<UploadOutlined />}
                            text={<Trans>Upload</Trans>}
                        />
                    </Button>
                </div>

                <Divider></Divider>
                <b><Trans>9. Review Reports from Third-Party Verifiers. (if any)</Trans></b>
                <Tooltip
                    placement="top"
                    title={
                        <>
                            <div>
                                Some organizations, such as Rainforest Alliance or Fairtrade International, provide independent verification of consultation processes. They evaluate whether indigenous peoples have been properly consulted and whether their rights have been respected.
                            </div>
                        </>
                    }
                    children={
                        <InfoCircleTwoTone
                            twoToneColor="#1890ff"
                            style={{ marginLeft: 5 }}
                        />
                    }
                />
                <Form.Item
                    style={{ marginBottom: 0 }}
                    name={["onboarding", "profile", "reviewReportsFromThirdPartyVerifiers"]}
                >
                    <div style={{ marginBottom: 10 }}>
                        <Upload {...reviewReportProps}>
                            <Button
                                style={{ marginTop: 10 }}
                                icon={<FolderOpenOutlined />}
                            > <Trans>Select File</Trans></Button>
                        </Upload>
                    </div>
                </Form.Item>
                <div className="center-card">
                    <Button
                        loading={reviewReportLoading}
                        htmlType="button"
                        type="primary"
                        onClick={() => uploadProfileDocument(
                            'onboarding.profile.reviewReportsFromThirdPartyVerifiers',
                            reviewReportFileList,
                            setReviewReportFileList,
                            setReviewReportLoading
                        )}
                    >
                        <ButtonWithIcon
                            icon={<UploadOutlined />}
                            text={<Trans>Upload</Trans>}
                        />
                    </Button>
                </div>
                <Divider></Divider>
                <b><Trans>11. Not all substantiated concerns or enforcement actions may be public. Do you have any, especially early-stage or ongoing investigations or any information that would point to a risk that the relevant products are non-compliant regarding EUDR?</Trans></b>
                <Form.Item
                    name={["onboarding", "profile", "haveAnyEarlyStageInvestigation"]}
                    rules={[{ required: true, message: 'Please select an option' }]}
                >
                    <Radio.Group style={{ marginTop: 10 }}>
                        <Space direction="horizontal">
                            <Radio value={1}>Yes</Radio>
                            <Radio value={2}>No</Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider></Divider>
                <b><Trans>12. Have any known risks, inspection findings, or alerts for your products, origins, or traders been published (e.g.: BMEL Germany, Nébih Hungary). Please write a few sentences about what it contains.</Trans></b>
                <Form.Item
                    style={{ marginBottom: 10 }}
                    name={["onboarding", "profile", "knownRisksForSpecificProducts"]}
                >
                    <TextArea rows={4} placeholder="What it contains..." style={{ marginTop: 10 }} />
                </Form.Item>

                <Divider></Divider>
                <b><Trans>13. If you have any certification, please provide it (upload) alongside with its identification number! Please write a few sentences about what it contains.</Trans></b>
                <Form.Item
                    style={{ marginBottom: 0 }}
                    name={["onboarding", "profile", "certification"]}
                >
                    <div style={{ marginBottom: 10 }}>
                        <Upload {...certificationProps}>
                            <Button
                                style={{ marginTop: 10 }}
                                icon={<FolderOpenOutlined />}
                            > <Trans>Select File</Trans></Button>
                        </Upload>
                    </div>
                </Form.Item>
                <Form.Item
                    style={{ marginBottom: 10 }}
                    name={["onboarding", "profile", "certId"]}>
                    <Input placeholder="Certification ID" style={{ marginTop: 10 }} />
                </Form.Item>
                <Form.Item
                    style={{ marginBottom: 10 }}
                    name={["onboarding", "profile", "certIdDescription"]}
                >
                    <TextArea rows={4} placeholder="Description...." />
                </Form.Item>
                <div className="center-card">
                    <Button
                        loading={certificationLoading}
                        htmlType="button"
                        type="primary"
                        onClick={() => uploadProfileDocument(
                            'onboarding.profile.certification',
                            certificationFileList,
                            setCertificationFileList,
                            setCertificationLoading
                        )}
                    >
                        <ButtonWithIcon
                            icon={<UploadOutlined />}
                            text={<Trans>Upload</Trans>}
                        />
                    </Button>
                </div>
            </Card>
        </Form>
    );
};

export default OnboardingSecondStep;